import React, { useState } from 'react';
import styled from 'styled-components';
import { Plus, Trash2, BellOff } from 'lucide-react';
import { backofficeApi, extractErrorMessage, formatDate } from '@kardume/services';
import { useNotification } from '@kardume/hooks';
import type { MessagingOptOutDto } from '@kardume/types';
import { Button, ActionButton, Modal, TextInput, PhoneInput, Table, TableContainer } from '@kardume/ui';
import { useAsync } from '../../utils/useAsync';
import DataState from '../DataState';

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-bottom: 1rem;
`;

const Explainer = styled.div`
  display: flex;
  gap: 0.7rem;
  border: 1px solid ${(p) => p.theme.border};
  border-left: 3px solid ${(p) => p.theme.error};
  border-radius: ${(p) => p.theme.radius};
  background: ${(p) => p.theme.surfaceAlt};
  padding: 0.85rem 1rem;
  margin-bottom: 1.25rem;
  font-size: 0.85rem;
  line-height: 1.55;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

const RowActions = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const ConfirmText = styled.p`
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.5;
  color: ${(p) => p.theme.textSecondary};

  strong {
    color: ${(p) => p.theme.textPrimary};
  }
`;

interface OptOutsTabProps {
  canManage: boolean;
}

/**
 * Números que pediram para não ser mais contatados.
 *
 * A lista é global e por número, não por lista de contatos: fosse por lista, a
 * próxima planilha importada traria a pessoa de volta - e é essa segunda mensagem,
 * depois do "pare", que vira denúncia e derruba o número.
 */
const OptOutsTab: React.FC<OptOutsTabProps> = ({ canManage }) => {
  const { showNotification } = useNotification();
  const { data, loading, error, reload } = useAsync(() => backofficeApi.listMessagingOptOuts(), []);

  const [isOpen, setIsOpen] = useState(false);
  const [phone, setPhone] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState<MessagingOptOutDto | null>(null);
  const [removing, setRemoving] = useState(false);

  const optOuts = data ?? [];

  const submit = async (): Promise<void> => {
    setSubmitting(true);
    try {
      await backofficeApi.addMessagingOptOut({ phoneNumber: phone, reason: reason.trim() || undefined });
      showNotification('Número registrado. Ele não entra mais em nenhuma campanha.', 'success');
      setIsOpen(false);
      setPhone('');
      setReason('');
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível registrar o opt-out.'), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const confirmRemove = async (): Promise<void> => {
    if (!deleting) return;
    setRemoving(true);
    try {
      await backofficeApi.deleteMessagingOptOut(deleting.id);
      showNotification('Opt-out removido. O número volta a receber campanhas.', 'success');
      setDeleting(null);
      reload();
    } catch (err) {
      showNotification(extractErrorMessage(err, 'Não foi possível remover o opt-out.'), 'error');
    } finally {
      setRemoving(false);
    }
  };

  return (
    <>
      <Explainer>
        <BellOff size={18} />
        <span>
          Vale para <strong>todas</strong> as listas e campanhas, agora e no futuro. Um contato que
          já está numa lista é marcado na hora; um importado depois já entra marcado.
        </span>
      </Explainer>

      {canManage && (
        <Toolbar>
          <Button variant="primary" onClick={() => setIsOpen(true)}>
            <Plus size={16} /> Registrar número
          </Button>
        </Toolbar>
      )}

      <DataState
        loading={loading}
        error={error}
        isEmpty={optOuts.length === 0}
        emptyTitle="Nenhum opt-out registrado"
        emptyDescription="Quando um lojista pedir para parar de receber, registre o número aqui."
        onRetry={reload}
      >
        <TableContainer>
          <Table>
            <thead>
              <tr>
                <th>WhatsApp</th>
                <th>Motivo</th>
                <th style={{ whiteSpace: 'nowrap' }}>Registrado em</th>
                <th style={{ width: '1%', whiteSpace: 'nowrap', textAlign: 'right' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {optOuts.map((optOut) => (
                <tr key={optOut.id}>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <strong>{optOut.phoneNumberFormatted}</strong>
                  </td>
                  <td>{optOut.reason || '-'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDate(optOut.createdAt)}</td>
                  <td>
                    <RowActions>
                      {canManage && (
                        <ActionButton
                          title="Remover opt-out"
                          aria-label="Remover opt-out"
                          color="#DC2626"
                          onClick={() => setDeleting(optOut)}
                        >
                          <Trash2 size={16} />
                        </ActionButton>
                      )}
                    </RowActions>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableContainer>
      </DataState>

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title="Registrar opt-out"
        width="440px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsOpen(false)} disabled={submitting}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={submit} isLoading={submitting} disabled={!phone}>
              Registrar
            </Button>
          </>
        }
      >
        <PhoneInput label="WhatsApp" value={phone} onChange={setPhone} />
        <TextInput
          label="Motivo (opcional)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Ex.: pediu para não receber mais"
          containerStyle={{ marginTop: '0.75rem' }}
        />
      </Modal>

      <Modal
        isOpen={deleting !== null}
        onClose={() => setDeleting(null)}
        title="Remover opt-out"
        width="440px"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleting(null)} disabled={removing}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={confirmRemove} isLoading={removing}>
              Remover
            </Button>
          </>
        }
      >
        <ConfirmText>
          Remover o opt-out de <strong>{deleting?.phoneNumberFormatted}</strong>? O número volta a
          entrar em campanhas. Só faça isso se a pessoa pediu para voltar a receber.
        </ConfirmText>
      </Modal>
    </>
  );
};

export default OptOutsTab;
